/* @jsxImportSource @emotion/react */

import React from 'react'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'
import { usePageContext } from 'vike-react/usePageContext'

import { SliteLogo } from '@slite/components'
import { LinkedInIcon, TwitterXIcon } from '@slite/icons'

import starRatingImg from '../../assets/stars-rating-4.9.png'
import { useRoutes } from '../../routes'
import { getUrl } from '../../utils/getUrl'
import { extractLocale } from '../../utils/i18n'
import { Section } from '../Section'
import { getFooterData } from './data'
import { SliteRatings } from './SliteRatings'

const CURRENT_YEAR = new Date().getFullYear()

type BaseColumnProps = {
  className?: string
  children: React.ReactNode
}

const Column = ({ children, className }: BaseColumnProps) => (
  <div
    className={classNames(
      className,
      'font-family-title flexbox flexbox-column'
    )}
  >
    {children}
  </div>
)

const ColumnList = ({ children }: BaseColumnProps) => (
  <ul className="font-family-system font-weight-regular flexbox flexbox-column">
    {children}
  </ul>
)

const ColumnTitle = ({ children }: BaseColumnProps) => (
  <div className="font-family-title font-size-7 shade-05 font-weight-medium">
    {children}
  </div>
)

export const Footer = ({
  className,
  background = 'shade-100',
  withLocales = true,
}: {
  className?: string
  background?: string
  withLocales?: boolean
}) => {
  const intl = useIntl()
  const routes = useRoutes()
  const { urlPathname } = usePageContext()

  const FOOTER_LINKS = [
    {
      icon: TwitterXIcon,
      service: 'twitter',
      href: routes.external.twitter.url,
    },
    {
      icon: LinkedInIcon,
      service: 'linkedin',
      href: routes.external.linkedin.url,
    },
  ]

  // Extract path without locale for language switching
  const { urlPathnameWithoutLocale } = extractLocale(urlPathname)

  const { footerColumnsData, footerBottomLinksData, footerLocales } =
    getFooterData(intl)

  return (
    <Section
      as="footer"
      background={background}
      className={classNames('font-family-title line-height-larger', className)}
    >
      <div className="grid-20 grid-row-gap-24 grid-column-gap-8">
        <Column className="grid-span-20 grid-span-10-medium grid-span-5-large">
          <div className="flexbox flexbox-column flexbox-gap-12 padding-right-40">
            <div className="flexbox flexbox-align-end ">
              <div className="flexbox flexbox-align-center">
                <SliteLogo
                  variant="symbol"
                  style={{ height: 28, width: 28 }}
                  className="margin-right-8"
                />
                <SliteLogo
                  variant="wordmark"
                  style={{ height: 28, width: (28 * 200) / 78 }}
                />
              </div>
            </div>
            <div
              className="font-size-6 padding-top-4 line-height-larger"
              style={{ maxWidth: 200 }}
            >
              <FormattedMessage
                id="footer.tagline"
                defaultMessage="The self-maintaining knowledge base"
              />
            </div>
            <div className={classNames('font-size-8 padding-top-12')}>
              <img
                loading="lazy"
                src={starRatingImg}
                alt={intl.formatMessage({
                  id: 'footer.sliteRating',
                  defaultMessage: 'Slite rating',
                })}
                width={104}
              />
              <SliteRatings className="margin-top-12" />
            </div>
          </div>
        </Column>

        {footerColumnsData.map((columnData, index) => (
          <Column
            className="grid-span-10 grid-span-5-medium grid-span-3-large"
            key={`${columnData.title}-${index}`}
          >
            <ColumnTitle>{columnData.title}</ColumnTitle>
            <ColumnList>
              {columnData.items.map((columnItems, subIndex) => (
                <li
                  key={`${columnData.title}-${index}-${subIndex}`}
                  className="margin-top-8"
                >
                  <a
                    key={columnItems.url}
                    className="transition font-size-7 pointer shade-05 pointer-on-hover"
                    href={columnItems.url}
                    rel="noreferrer"
                  >
                    {columnItems.title}
                  </a>
                </li>
              ))}
            </ColumnList>
          </Column>
        ))}
      </div>

      <div className=" border-bottom-small border-alpha-shade-40 padding-bottom-16 flexbox flexbox-align-center  flexbox-column-gap-12 flexbox-justify-end">
        {FOOTER_LINKS.map((link, index) => {
          const IconLink = link.icon

          return (
            <a
              key={`${link.href}-${index}`}
              href={link.href}
              className="shade-05 pointer-on-hover transition"
              aria-label={intl.formatMessage(
                {
                  id: `footer.socialLink`,
                  defaultMessage: 'Go to {service}',
                },
                { service: link.service }
              )}
            >
              <IconLink size={16} />
            </a>
          )
        })}
      </div>

      <div className="grid-12 grid-row-gap-24 padding-top-32">
        <ul className="grid-span-9-not-small grid-span-12 flexbox flexbox-wrap flexbox-gap-24 ">
          {footerBottomLinksData.map((bottomLink, index) => (
            <li
              className="font-size-8 font-weight-medium"
              key={`${bottomLink.title}-${index}`}
            >
              <a
                key={bottomLink.url}
                className="shade-05 pointer-on-hover transition font-size-7 font-family-title pointer"
                href={bottomLink.url}
                target="_blank"
                rel="noreferrer"
              >
                {bottomLink.title}
              </a>
            </li>
          ))}
        </ul>
        <div className="grid-span-3-not-small grid-span-12 font-size-7 flexbox flexbox-align-center flexbox-justify-end">
          <FormattedMessage
            id="footer.copyright"
            defaultMessage={'©{year} Slite, Inc.'}
            values={{ year: CURRENT_YEAR }}
          />
        </div>
      </div>
      <div className="flexbox flexbox-space-between flexbox-align-center padding-top-24 flexbox-column-small flexbox-align-start flexbox-row-gap-24">
        {withLocales && (
          <ul className="flexbox flexbox-wrap flexbox-gap-16">
            {footerLocales.map((localeOption) => (
              <li key={localeOption.id}>
                <a
                  href={getUrl({
                    locale: localeOption.id,
                    path: urlPathnameWithoutLocale,
                  })}
                  className={classNames(
                    'transition font-size-7 font-family-title pointer',
                    localeOption.id === intl.locale
                      ? 'shade-00 font-weight-medium'
                      : 'shade-05 pointer-on-hover shade-00-on-hover'
                  )}
                  aria-current={
                    localeOption.id === intl.locale ? 'true' : undefined
                  }
                >
                  {localeOption.defaultMessage}
                </a>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          className="shade-05 font-size-7 shade-10-on-hover border-none background-transparent pointer cky-banner-element"
        >
          <FormattedMessage
            defaultMessage="Cookie settings"
            id="slite.footer.cookieSettings"
          />
        </button>
      </div>
    </Section>
  )
}
